import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Home, Users, Clock, Shield } from "lucide-react";
import { createServiceSchema, createFAQSchema } from "../seo/schemas";

const HouseRemovalsPage = () => {
  const features = [
    { icon: Users, title: "Professional Team", desc: "Trained, uniformed movers who treat your belongings with care" },
    { icon: Clock, title: "Flexible Scheduling", desc: "Morning, afternoon, or weekend moves to suit your timeline" },
    { icon: Shield, title: "Fully Insured", desc: "Comprehensive coverage up to £50,000 as standard" },
    { icon: Home, title: "All Property Types", desc: "From studio flats to 6+ bedroom family homes" }
  ];

  const included = [
    "Free, no-obligation quote",
    "Packing materials available",
    "Furniture dismantling & reassembly",
    "Floor & door frame protection",
    "Blanket wrapping for furniture",
    "Removal of packing waste",
    "Storage options available",
    "Same-day service available"
  ];

  const breadcrumbs = [
    { label: "Services", href: "/services" },
    { label: "House Removals", href: "/services/house-removals" },
  ];

  const pageFaqs = [
    { q: "How much do house removals cost in Cardiff?", a: "House removal costs in Cardiff typically range from £300 for a studio flat to £1,500+ for a large family home. Get an instant estimate with our online calculator, or book a free survey for a fixed-price quote." },
    { q: "How far in advance should I book my house move?", a: "We recommend booking 2-4 weeks ahead, especially for weekends. However, we can often accommodate last-minute and same-day moves — just call us on 07346 209593." },
    { q: "Do you dismantle and reassemble furniture?", a: "Yes, furniture dismantling and reassembly is included as standard. Our team brings all the tools needed to handle beds, wardrobes, desks and more." },
    { q: "Is my move fully insured?", a: "Yes, all house removals are fully insured up to £50,000 as standard. Additional cover is available for high-value items." },
  ];

  const serviceSchema = createServiceSchema(
    "House Removals Cardiff",
    "Professional house removal service in Cardiff and South Wales. From studio flats to 5-bed homes. Fully insured, packing available.",
    "/services/house-removals"
  );

  const jsonLd = [
    serviceSchema,
    createFAQSchema(pageFaqs),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="House Removals Cardiff | Professional Movers | Hitech Transit"
        description="Trusted house removals in Cardiff & South Wales. Fully insured, fixed-price quotes. From studio flats to 5-bed homes. Packing service available. Get your free quote."
        canonical="/services/house-removals"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={breadcrumbs} />
        {/* Hero */}
        <section className="relative py-20 lg:py-32">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/d9b70dc68cc964050d6b7582cabdba6cd06668dbb9ef9a403050982926bf0037.png)`
            }}
          >
            <div className="absolute inset-0 bg-[#0F172A]/70" />
          </div>
          
          <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                House Removals
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Professional House Removals in Cardiff
              </h1>
              <p className="text-slate-200 text-lg mb-8">
                Whether you're moving across the street or across the country, our experienced team 
                ensures your home move is smooth, safe, and on time.
              </p>
              <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="house-removals-quote-btn">
                Get Your Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={feature.title || feature.name || `feature-${index}`} className="text-center">
                  <div className="w-14 h-14 bg-[#FF5A1F]/10 mx-auto flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-[#FF5A1F]" />
                  </div>
                  <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="section-padding">
          <div className="container-max">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="label-text">What's Included</span>
                <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[#0F172A] mt-2 mb-6">
                  Complete House Moving Service
                </h2>
                <p className="text-slate-600 leading-relaxed mb-8">
                  Our house removal service covers everything you need for a seamless move. 
                  From careful packing to safe transportation and setup at your new home.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {included.map((item, index) => (
                    <div key={`included-${index}`} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1585458808362-e5b83a52178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtb3ZlcnMlMjBwYWNraW5nJTIwYm94ZXN8ZW58MHx8fHwxNzc1NTg0NTMxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Professional movers"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="section-padding bg-[#0F172A]">
          <div className="container-max">
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-2 block">Pricing</span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white">
                Transparent Pricing
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { size: "1-2 Bedroom", from: "£250" },
                { size: "3-4 Bedroom", from: "£450" },
                { size: "5+ Bedroom", from: "£750" }
              ].map((price, index) => (
                <div key={`price-${index}`} className="bg-white/5 border border-white/10 p-8 text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">{price.size}</h3>
                  <div className="text-4xl font-['Outfit'] font-bold text-[#FF5A1F] mb-2">
                    {price.from}
                  </div>
                  <div className="text-slate-400 text-sm">Starting from</div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-slate-400 text-sm mb-6">
                Final price depends on distance, access, and additional services required.
              </p>
              <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="house-removals-pricing-cta">
                Get Exact Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-white">
          <div className="container-max">
            <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
              Frequently Asked Questions About House Removals
            </h2>
            <div className="max-w-3xl space-y-6">
              {pageFaqs.map((faq, index) => (
                <div key={`faq-${index}`} className="border-b border-slate-200 pb-6">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HouseRemovalsPage;
